import { useState, useEffect, useRef } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import logo from '../assets/logosmall.png'
import './LandingPage.css'

function LandingPage() {
  const { isAuthenticated, isLoading } = useAuth()
  const [navScrolled, setNavScrolled] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const navRef = useRef(null)

  // Scroll-triggered navbar background
  useEffect(() => {
    if (isLoading || isAuthenticated) return
    const handleScroll = () => {
      setNavScrolled(window.scrollY > 40)
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [isLoading, isAuthenticated])

  // IntersectionObserver for fade-in sections
  useEffect(() => {
    if (isLoading || isAuthenticated) return
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible')
          }
        })
      },
      { threshold: 0.15 }
    )

    const elements = document.querySelectorAll('.landing-fade-in')
    elements.forEach((el) => observer.observe(el))

    return () => observer.disconnect()
  }, [isLoading, isAuthenticated])

  // Close mobile menu on outside click
  useEffect(() => {
    if (!menuOpen) return
    const handleClickOutside = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [menuOpen])

  if (isLoading) return null
  if (isAuthenticated) return <Navigate to="/dashboard" replace />

  const scrollTo = (id) => {
    setMenuOpen(false)
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <div className="landing-page">
      {/* ── Navbar ── */}
      <nav ref={navRef} className={`landing-nav ${navScrolled ? 'scrolled' : ''}`}>
        <Link to="/" className="landing-nav-brand">
          <img src={logo} alt="Frates.Cloud" className="landing-nav-logo" />
        </Link>

        <ul className={`landing-nav-links ${menuOpen ? 'open' : ''}`}>
          <li>
            <button className="landing-nav-link" onClick={() => scrollTo('features')}>
              Features
            </button>
          </li>
          <li>
            <button className="landing-nav-link" onClick={() => scrollTo('how-it-works')}>
              How It Works
            </button>
          </li>
          <li>
            <button className="landing-nav-link" onClick={() => scrollTo('stats')}>
              Why Us
            </button>
          </li>
          <li className="landing-nav-cta">
            <Link to="/login" className="landing-btn-outline">Sign In</Link>
            <Link to="/register" className="landing-btn-primary">Get Started</Link>
          </li>
        </ul>

        <button
          className="landing-hamburger"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
          aria-expanded={menuOpen}
        >
          <span />
          <span />
          <span />
        </button>
      </nav>

      {/* ── Hero ── */}
      <section className="landing-hero">
        <div className="landing-hero-pattern" />

        {/* Freight-themed animated elements */}
        <div className="landing-hero-shapes">
          {/* Animated shipping route lines */}
          <svg className="landing-route-svg" viewBox="0 0 1440 800" preserveAspectRatio="none">
            <path className="landing-route-path" d="M-50,400 C200,350 400,500 700,300 S1100,450 1500,250" />
            <path className="landing-route-path landing-route-path-2" d="M-50,550 C300,500 500,350 800,500 S1200,300 1500,450" />
            <path className="landing-route-path landing-route-path-3" d="M-50,250 C250,300 450,150 750,350 S1050,200 1500,350" />
            {/* Route dots moving along paths */}
            <circle className="landing-route-dot" r="4">
              <animateMotion dur="8s" repeatCount="indefinite" path="M-50,400 C200,350 400,500 700,300 S1100,450 1500,250" />
            </circle>
            <circle className="landing-route-dot landing-route-dot-2" r="3">
              <animateMotion dur="10s" repeatCount="indefinite" path="M-50,550 C300,500 500,350 800,500 S1200,300 1500,450" />
            </circle>
            <circle className="landing-route-dot landing-route-dot-3" r="3.5">
              <animateMotion dur="12s" repeatCount="indefinite" path="M-50,250 C250,300 450,150 750,350 S1050,200 1500,350" />
            </circle>
          </svg>

          {/* Freight icons floating */}
          <div className="landing-freight-icon landing-freight-ship">&#x1F6A2;</div>
          <div className="landing-freight-icon landing-freight-plane">&#x2708;&#xFE0F;</div>
          <div className="landing-freight-icon landing-freight-truck">&#x1F69A;</div>
          <div className="landing-freight-icon landing-freight-container">&#x1F4E6;</div>
          <div className="landing-freight-icon landing-freight-globe">&#x1F30D;</div>

          {/* Location pins */}
          <div className="landing-pin landing-pin-1">
            <div className="landing-pin-dot" />
            <div className="landing-pin-pulse" />
          </div>
          <div className="landing-pin landing-pin-2">
            <div className="landing-pin-dot" />
            <div className="landing-pin-pulse" />
          </div>
          <div className="landing-pin landing-pin-3">
            <div className="landing-pin-dot" />
            <div className="landing-pin-pulse" />
          </div>
        </div>

        <div className="landing-hero-content">
          <div className="landing-hero-badge">
            AI-Powered Freight Management
          </div>
          <h1>
            Freight Quoting<br />
            Made <span>Effortless</span>
          </h1>
          <p className="landing-hero-subtitle">
            Submit RFQs, let AI extract the details, and receive carrier quotes
            automatically. Streamline your entire freight workflow in one platform.
          </p>
          <div className="landing-hero-actions">
            <Link to="/register" className="landing-hero-btn-primary">
              Get Started Free
              <span>&rarr;</span>
            </Link>
            <button
              className="landing-hero-btn-secondary"
              onClick={() => scrollTo('how-it-works')}
            >
              See How It Works
            </button>
          </div>
        </div>
      </section>

      {/* ── Features ── */}
      <section id="features" className="landing-features">
        <div className="landing-section-header landing-fade-in">
          <span className="landing-section-label">Features</span>
          <h2 className="landing-section-title">
            Everything you need to manage freight quotes
          </h2>
          <p className="landing-section-subtitle">
            From AI-powered data extraction to automated carrier communication,
            we handle the complexity so you can focus on your business.
          </p>
        </div>

        <div className="landing-features-grid landing-fade-in">
          <div className="landing-feature-card">
            <div className="landing-feature-icon blue">
              <span role="img" aria-label="AI">&#x1F9E0;</span>
            </div>
            <h3>AI-Powered RFQ Processing</h3>
            <p>
              Submit unstructured RFQ text and our AI automatically extracts
              origins, destinations, cargo details, and requirements.
            </p>
          </div>

          <div className="landing-feature-card">
            <div className="landing-feature-icon sky">
              <span role="img" aria-label="Carriers">&#x1F6A2;</span>
            </div>
            <h3>Multi-Carrier Management</h3>
            <p>
              Manage your carrier registry with support for ocean, air, rail,
              and road carriers — all in one place.
            </p>
          </div>

          <div className="landing-feature-card">
            <div className="landing-feature-icon green">
              <span role="img" aria-label="Tracking">&#x1F4CA;</span>
            </div>
            <h3>Real-Time Quote Tracking</h3>
            <p>
              Track every quote request from submission to acceptance. See
              statuses, prices, and transit times at a glance.
            </p>
          </div>

          <div className="landing-feature-card">
            <div className="landing-feature-icon amber">
              <span role="img" aria-label="Email">&#x2709;&#xFE0F;</span>
            </div>
            <h3>Automated Communication</h3>
            <p>
              RFQs are automatically sent to carriers via email. Responses
              are parsed by AI and linked back to your requests.
            </p>
          </div>
        </div>
      </section>

      {/* ── How It Works ── */}
      <section id="how-it-works" className="landing-how-it-works">
        <div className="landing-section-header landing-fade-in">
          <span className="landing-section-label">How It Works</span>
          <h2 className="landing-section-title">
            Three steps to streamlined quoting
          </h2>
          <p className="landing-section-subtitle">
            Get from RFQ to quote in minutes, not days.
          </p>
        </div>

        <div className="landing-steps landing-fade-in">
          <div className="landing-step">
            <div className="landing-step-number">1</div>
            <div className="landing-step-icon">&#x1F4DD;</div>
            <h3>Submit Your RFQ</h3>
            <p>
              Paste or type your freight request in plain text. No forms to
              fill, no templates required.
            </p>
          </div>

          <div className="landing-step">
            <div className="landing-step-number">2</div>
            <div className="landing-step-icon">&#x2728;</div>
            <h3>AI Processes &amp; Sends</h3>
            <p>
              Our AI extracts structured data and automatically forwards the
              RFQ to your selected carrier.
            </p>
          </div>

          <div className="landing-step">
            <div className="landing-step-number">3</div>
            <div className="landing-step-icon">&#x2705;</div>
            <h3>Receive &amp; Compare</h3>
            <p>
              Carrier replies are parsed automatically. Review quotes, accept
              the best offer, and export as PDF.
            </p>
          </div>
        </div>
      </section>

      {/* ── Stats ── */}
      <section id="stats" className="landing-stats landing-fade-in">
        <div className="landing-stats-grid">
          <div className="landing-stat">
            <div className="landing-stat-number">10x</div>
            <div className="landing-stat-label">Faster quoting process</div>
          </div>
          <div className="landing-stat">
            <div className="landing-stat-number">100%</div>
            <div className="landing-stat-label">Automated data extraction</div>
          </div>
          <div className="landing-stat">
            <div className="landing-stat-number">24/7</div>
            <div className="landing-stat-label">Always-on carrier communication</div>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="landing-cta landing-fade-in">
        <div className="landing-cta-content">
          <h2>Ready to streamline your freight quoting?</h2>
          <p>
            Join forward-thinking logistics teams who save hours every week
            with AI-powered freight management.
          </p>
          <Link to="/register" className="landing-cta-btn">
            Get Started Free
            <span>&rarr;</span>
          </Link>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="landing-footer">
        <div className="landing-footer-content">
          <div className="landing-footer-brand">
            <h3>Frates.Cloud</h3>
            <p>
              AI-powered freight quoting and carrier management platform.
              Simplifying global logistics, one quote at a time.
            </p>
          </div>

          <div className="landing-footer-col">
            <h4>Product</h4>
            <ul>
              <li><button className="landing-nav-link" onClick={() => scrollTo('features')}>Features</button></li>
              <li><button className="landing-nav-link" onClick={() => scrollTo('how-it-works')}>How It Works</button></li>
              <li><Link to="/register">Sign Up</Link></li>
            </ul>
          </div>

          <div className="landing-footer-col">
            <h4>Company</h4>
            <ul>
              <li><a href="#about">About Us</a></li>
              <li><a href="#privacy">Privacy Policy</a></li>
              <li><a href="#terms">Terms of Service</a></li>
            </ul>
          </div>

          <div className="landing-footer-col">
            <h4>Support</h4>
            <ul>
              <li><a href="mailto:support@frates.cloud">support@frates.cloud</a></li>
              <li><a href="#docs">Documentation</a></li>
              <li><a href="#help">Help Center</a></li>
            </ul>
          </div>
        </div>

        <div className="landing-footer-bottom">
          <p>&copy; {new Date().getFullYear()} Frates.Cloud. All rights reserved.</p>
          <p>Built for modern freight teams</p>
        </div>
      </footer>
    </div>
  )
}

export default LandingPage
